import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Theme = "light" | "dark";
export type PanelState = "open" | "rail" | "hidden";
export type Density = "comfortable" | "compact";
export type ProseFont = "serif" | "sans";
export type Scope = "doc" | "col" | "all";

interface UiState {
  theme: Theme;
  sidebarCollapsed: boolean;
  aiPanel: PanelState;
  chatPanel: PanelState;
  density: Density;
  proseFont: ProseFont;
  showAnnotations: boolean;
  /** Margin chips visibility — uiv2 decision 2 (2026-05-25): ON by default. */
  marginChips: boolean;
  readingMode: boolean;
  locale: "es" | "en";
  /** Global scope for ask-bar / search / chat. uiv2 decision 4: ask-bar → /chat. */
  scope: Scope;
  /** Pre-filled chat draft (e.g. from Home ask-bar). Consumed once by ChatRail. */
  chatDraft: string | null;
  setTheme: (theme: Theme) => void;
  cycleTheme: () => void;
  toggleSidebar: () => void;
  setAiPanel: (s: PanelState) => void;
  toggleAiPanel: () => void;
  setChatPanel: (s: PanelState) => void;
  toggleChatPanel: () => void;
  setDensity: (d: Density) => void;
  setProseFont: (f: ProseFont) => void;
  setShowAnnotations: (b: boolean) => void;
  setMarginChips: (b: boolean) => void;
  toggleMarginChips: () => void;
  setReadingMode: (b: boolean) => void;
  setLocale: (l: "es" | "en") => void;
  setScope: (s: Scope) => void;
  setChatDraft: (draft: string | null) => void;
}

export const useUI = create<UiState>()(
  persist(
    (set, get) => ({
      theme: "light",
      sidebarCollapsed: false,
      aiPanel: "open",
      chatPanel: "rail",
      density: "comfortable",
      proseFont: "serif",
      showAnnotations: true,
      marginChips: true,
      readingMode: false,
      locale: "es",
      scope: "col",
      chatDraft: null,
      setTheme: (theme) => set({ theme }),
      cycleTheme: () => {
        const cur = get().theme;
        set({ theme: cur === "light" ? "dark" : "light" });
      },
      toggleSidebar: () => set({ sidebarCollapsed: !get().sidebarCollapsed }),
      setAiPanel: (s) => set({ aiPanel: s }),
      toggleAiPanel: () => set({ aiPanel: get().aiPanel === "open" ? "rail" : "open" }),
      setChatPanel: (s) => set({ chatPanel: s }),
      toggleChatPanel: () => set({ chatPanel: get().chatPanel === "open" ? "rail" : "open" }),
      setDensity: (density) => set({ density }),
      setProseFont: (proseFont) => set({ proseFont }),
      setShowAnnotations: (b) => set({ showAnnotations: b }),
      setMarginChips: (b) => set({ marginChips: b }),
      toggleMarginChips: () => set({ marginChips: !get().marginChips }),
      setReadingMode: (b) => set({ readingMode: b }),
      setLocale: (locale) => set({ locale }),
      setScope: (scope) => set({ scope }),
      setChatDraft: (chatDraft) => set({ chatDraft }),
    }),
    {
      name: "docustudio-ui",
      version: 4,
      // chatDraft is ephemeral; don't persist it.
      partialize: (s) => {
        const { chatDraft: _, ...rest } = s as UiState & Record<string, unknown>;
        return rest;
      },
    },
  ),
);
